import React, { useState } from 'react';
import { User, UserSpeaker, Speaker } from '../../types/conference';
import { API_BASE_URL } from '../../config';
import SpeakerManagement from './SpeakerManagement';
import { Dialog } from '@headlessui/react';

interface UserManagementProps {
  users: User[];
  speakers: UserSpeaker[];
  onUpdateUser: (user: User) => Promise<void>;
  onDeleteUser: (userId: string) => Promise<void>;
  onPromoteToSpeaker: (userId: string) => Promise<void>;
  onUpdateSpeaker: (speaker: Speaker) => Promise<void>;
  onDeleteSpeaker: (id: string) => Promise<void>;
}

const UserManagement: React.FC<UserManagementProps> = ({
  users = [],
  speakers: userSpeakers = [],
  onUpdateUser,
  onDeleteUser,
  onPromoteToSpeaker,
  onUpdateSpeaker,
  onDeleteSpeaker,
}) => {
  const [selectedTab, setSelectedTab] = useState<'users' | 'speakers'>('users');
  const [searchTerm, setSearchTerm] = useState('');

  // Map UserSpeakers to Speakers with correct IDs (use speaker_id instead of user id)
  const speakersForManagement: Speaker[] = userSpeakers.map(us => ({
    id: us.speaker_id ? String(us.speaker_id) : us.id,
    name: us.name,
    email: us.email,
    bio: us.bio || 'No bio available',
    company: us.company || 'N/A',
    title: us.title,
    role: us.title || 'Speaker',
    image: us.image_url,
    linkedin_url: us.linkedin_url,
    display_on_public: us.display_on_public,
    bio_short: us.bio_short,
    talk_title: us.talk_title,
    talk_abstract: us.talk_abstract,
  }));

  const filteredUsers = users.filter(
    (user) =>
      (user.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const handlePromote = async (userId: string) => {
    try {
      await onPromoteToSpeaker(userId);
    } catch (error) {
      console.error('Failed to promote user to speaker:', error);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex" aria-label="Tabs">
          <button
            onClick={() => setSelectedTab('users')}
            className={`${selectedTab === 'users' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} w-1/2 py-4 px-1 text-center border-b-2 font-medium text-sm`}>
            Users ({users.length})
          </button>
          <button
            onClick={() => setSelectedTab('speakers')}
            className={`${selectedTab === 'speakers' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} w-1/2 py-4 px-1 text-center border-b-2 font-medium text-sm`}>
            Speakers ({userSpeakers.length})
          </button>
        </nav>
      </div>

      {/* Search - Only shown for Users tab */}
      {selectedTab === 'users' && (
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="overflow-x-auto">
        {selectedTab === 'users' ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th scope="col" className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-xs text-gray-500">No Photo</span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.role}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {!user.is_speaker && (
                      <button
                        onClick={() => handlePromote(user.id)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        Promote to Speaker
                      </button>
                    )}
                    <button
                      onClick={() => onDeleteUser(user.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-4">
            <SpeakerManagement
              speakers={speakersForManagement}
              onUpdateSpeaker={onUpdateSpeaker}
              onDeleteSpeaker={onDeleteSpeaker}
              onCreateSpeaker={async (speaker) => { console.log('Create speaker not implemented from this component'); }}
              onImageUpload={async (speakerId, file) => {
                try {
                  const formData = new FormData();
                  formData.append('file', file);

                  const response = await fetch(`${API_BASE_URL}/api/admin/speakers/${speakerId}/upload-image`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    },
                    body: formData,
                  });

                  if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || 'Failed to upload image');
                  }

                  const result = await response.json();
                  // Update the speaker with the new image filename
                  const updatedSpeakers = userSpeakers.map((s: UserSpeaker) => 
                    s.id === speakerId ? { ...s, image_url: result.filename } : s
                  );
                  // Trigger a refresh by calling onUpdateSpeaker with updated data
                  const updatedSpeaker = updatedSpeakers.find((s: UserSpeaker) => s.id === speakerId);
                  if (updatedSpeaker) {
                    await onUpdateSpeaker(updatedSpeaker);
                  }
                } catch (error) {
                  console.error('Error uploading image:', error);
                  throw error;
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
